import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { DeliveryStatus } from "./labels";

export type DeliveryRecord = components["schemas"]["DeliveryRecord"];

/** One delivery for its page (P8-48): the board's row and the trip, crew, lines, events, incidents. */
export const deliveryQuery = (id: string) =>
  queryOptions({
    queryKey: ["delivery", id],
    queryFn: () => ok(api.GET("/v1/admin/dispatch/deliveries/{id}", { params: { path: { id } } })),
  });

/** A delivery's page, under its order: Operations › Orders › ORD-1042 › DEL-2210. */
export const deliveryHref = (orderId: string, deliveryId: string) => `/orders/${orderId}/deliveries/${deliveryId}`;

/** The trip, in order. A parcel (no fill site) skips the fill site's two steps. */
const TRIP = [
  { key: "pending", label: "Placed" },
  { key: "fillsite_assigned", label: "Fill site" },
  { key: "assigned", label: "Assigned" },
  { key: "accepted", label: "Accepted" },
  { key: "at_fillsite", label: "At fill site" },
  { key: "in_transit", label: "On the way" },
  { key: "at_customer", label: "At customer" },
  { key: "delivered", label: "Delivered" },
] as const satisfies readonly { key: DeliveryStatus; label: string }[];

const FILLSITE_STEPS = new Set<DeliveryStatus>(["fillsite_assigned", "at_fillsite"]);
/** Where the trip stops for a status off the main line: suspended happens at the customer. */
const STOPPED: Partial<Record<DeliveryStatus, DeliveryStatus | null>> = {
  suspended: "at_customer",
  failed: null,
  cancelled: null,
  rerouted: null,
};

export interface TripStep {
  key: DeliveryStatus;
  label: string;
  /** when it got there: the latest event into the step (the creation for Placed) */
  at: string | null;
  state: "done" | "now" | "todo";
}

/**
 * The delivery's steps for its tracker, from its status and events. A delivery off the main line
 * (suspended, failed, cancelled, rerouted) is at the last step it reached, and `stopped` says why.
 */
export function tripOf(d: Pick<DeliveryRecord, "status" | "events" | "created_at" | "assignment">) {
  const reached = new Set(d.events.map((e) => e.to_status));
  const load = Boolean(d.assignment?.fillsite) || reached.has("fillsite_assigned") || reached.has("at_fillsite");
  const steps = TRIP.filter((s) => load || !FILLSITE_STEPS.has(s.key));
  const keys = steps.map((s) => s.key as DeliveryStatus);
  const offLine = d.status in STOPPED;
  let current = keys.indexOf(d.status);
  if (offLine) {
    const at = STOPPED[d.status];
    // the furthest step it reached before it stopped
    current = at ? keys.indexOf(at) : Math.max(0, ...keys.map((k, i) => (reached.has(k) ? i : 0)));
  }
  const timeOf = (key: DeliveryStatus) =>
    key === "pending" ? d.created_at : (d.events.find((e) => e.to_status === key)?.occurred_at ?? null);
  return {
    steps: steps.map(
      (s, i): TripStep => ({
        key: s.key,
        label: s.label,
        at: i <= current ? timeOf(s.key) : null,
        state: i < current || (i === current && d.status === "delivered") ? "done" : i === current ? "now" : "todo",
      }),
    ),
    stopped: offLine ? d.status : null,
  };
}

/** The order page's five-part bar: planned, assigned, picked up, on the road, delivered. */
const BAR: DeliveryStatus[][] = [
  ["fillsite_assigned"],
  ["assigned"],
  ["accepted", "at_fillsite"],
  ["in_transit", "at_customer", "suspended"],
  ["delivered"],
];
const RANK: DeliveryStatus[] = [
  "pending",
  "fillsite_assigned",
  "assigned",
  "accepted",
  "at_fillsite",
  "in_transit",
  "at_customer",
  "delivered",
];

/** How far along the bar a delivery is, and its tone: moving, done, failed, or stopped (grey). */
export function progressOf(status: DeliveryStatus): {
  done: number;
  of: number;
  tone: "moving" | "done" | "failed" | "stopped";
} {
  if (status === "delivered") return { done: BAR.length, of: BAR.length, tone: "done" };
  if (status === "failed") return { done: BAR.length, of: BAR.length, tone: "failed" };
  if (status === "cancelled" || status === "rerouted") return { done: 0, of: BAR.length, tone: "stopped" };
  const rank = RANK.indexOf(status === "suspended" ? "at_customer" : status);
  const done = BAR.filter((group) => group.some((s) => RANK.indexOf(s) <= rank && s !== "suspended")).length;
  return { done, of: BAR.length, tone: "moving" };
}

// --- the proof-of-delivery checklist (P8-49) -----------------------------------------------

export type ProofItem = components["schemas"]["ProofChecklistItem"];
export type ProofKind = ProofItem["kind"];
export type ProofAnswer = components["schemas"]["ProofAnswer"];

export const PROOF_KIND: Record<ProofKind, string> = {
  photo: "Photo",
  signature: "Signature",
  yes_no: "Yes or no",
  number: "Number",
  text: "Text",
};

/** Photos and signatures are files; staff completing for a driver may leave them out. */
export const isFileKind = (kind: ProofKind) => kind === "photo" || kind === "signature";

/** An answer as words, for the proof views; files show as images instead. */
export function answerText(a: ProofAnswer): string {
  if (a.bool_value !== null) return a.bool_value ? "Yes" : "No";
  if (a.number_value !== null) return String(a.number_value);
  if (a.text_value !== null) return a.text_value;
  return a.kind === "signature" ? "Signed" : "Photo";
}

export const proofChecklistQuery = queryOptions({
  queryKey: ["proof-checklist"],
  queryFn: () => ok(api.GET("/v1/admin/proof-checklist")),
});

const path = (id: string) => ({ params: { path: { id } } });

export const proofChecklistActions = {
  createItem: (body: components["schemas"]["ProofChecklistItemBody"]) =>
    ok(api.POST("/v1/admin/proof-checklist/items", { body })),
  updateItem: (id: string, body: components["schemas"]["UpdateProofChecklistItemBody"]) =>
    ok(api.PATCH("/v1/admin/proof-checklist/items/{id}", { ...path(id), body })),
  archiveItem: (id: string) => ok(api.POST("/v1/admin/proof-checklist/items/{id}/archive", path(id))),
  reorder: (ids: string[]) => ok(api.PUT("/v1/admin/proof-checklist/items/order", { body: { ids } })),
};

/** While a delivery is between accepted and at the customer its truck moves: its page polls (P8-51). */
const LIVE = new Set<DeliveryStatus>(["accepted", "at_fillsite", "in_transit", "at_customer"]);
export const isLive = (status: DeliveryStatus) => LIVE.has(status);

export type Eta = NonNullable<DeliveryRecord["eta"]>;

/**
 * A delivery's ETA on the way (P8-73), as the console words it: "ETA 10:59 · in 7 min · 3.1 km by
 * road" and its source. The time is in the company's zone.
 */
export function etaText(eta: Eta, timezone: string) {
  const clock = new Intl.DateTimeFormat("en-KE", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(eta.arrives_at));
  // number-ok: metres to km for display, not money
  const km = (eta.distance_m / 1000).toFixed(1);
  return {
    clock,
    short: `ETA ${clock}, in ${eta.minutes} min`,
    line: `ETA ${clock} · in ${eta.minutes} min · ${km} km by road${eta.source === "google" ? ", with traffic (Google)" : " (estimate)"}`,
  };
}
